import React, { useState, useContext } from 'react'
import ReactDOM from 'react-dom'
import { useMutation } from '@apollo/client'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import languages, { getLanguageAcronym } from '../languages.js'
import { ADD_MEETING_WITH_CONTENT_MUTATION } from '../query/gqlQueries.js'
import { streamOllamaGenerate } from '../ollama.js'
import useLearnerContext from '../hooks/useLearnerContext.js'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import styles from './AddProjectDialog.module.scss'
import CloseIcon from '@mui/icons-material/Close'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'

const parseLoose = (raw) => {
  let s = String(raw || '').replace(/```json/gi, '').replace(/```/g, '').trim()
  const a = s.indexOf('{'), b = s.lastIndexOf('}')
  if (a >= 0 && b > a) s = s.slice(a, b + 1)
  try { const o = JSON.parse(s); return Array.isArray(o?.sentences) ? o.sentences : [] } catch { return [] }
}

export default function IntroduceYourselfDialog({ onPractice }) {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)
  const { user, learning, personalize } = useLearnerContext(username)
  const isOpen = dialogStates['dialogIntroduce']

  const learningNames = (learning || []).map(l => l.name).filter(Boolean)
  const [targetLanguage, setTargetLanguage] = useState('')
  const [status, setStatus] = useState('idle') // idle | generating | ready | saving | saved | error
  const [errorMsg, setErrorMsg] = useState('')
  const [progress, setProgress] = useState('')
  const [sentences, setSentences] = useState([])

  const [addMeetingWithContent] = useMutation(ADD_MEETING_WITH_CONTENT_MUTATION)

  // Default the language to the first one they're learning.
  const effLang = targetLanguage || learningNames[0] || 'Turkish'
  const code = getLanguageAcronym(effLang) || 'tur'
  const name = user?.name || 'the learner'

  const close = () => {
    closeDialog('dialogIntroduce')
    setStatus('idle'); setErrorMsg(''); setProgress(''); setSentences([]); setTargetLanguage('')
  }

  const generate = async () => {
    setStatus('generating'); setErrorMsg(''); setProgress('Writing your introduction…')
    const serverUrl = process.env.REACT_APP_OLLAMA_URI || 'http://localhost:11434'
    const level = (learning.find(l => l.name === effLang) || {}).level || 'A1'
    const personal = personalize(effLang)
    const prompt = `You are a warm ${effLang} teacher. Write a natural, FIRST-PERSON self-introduction that the learner can say aloud, in ${effLang}, pitched to CEFR level ${level}.
${personal}
Write 6-10 short, natural sentences a ${level} learner can actually pronounce (greeting, name, age, where they live, job, hobbies, why they're learning). Skip any facts that are missing. Keep sentences simple and true to the facts above — do not invent details.
Return ONLY JSON, no markdown: {"sentences":[{"tr":"<the ${effLang} sentence>","en":"<English translation>"}]}`
    try {
      const raw = await streamOllamaGenerate({
        serverUrl, apiKey: process.env.REACT_APP_OLLAMA_KEY,
        body: { model: 'qwen2.5:14b', prompt, format: 'json', options: { temperature: 0.4, num_predict: 1400 } },
        onProgress: (acc) => setProgress(`Writing your introduction… (${acc.length} chars)`),
      })
      const list = parseLoose(raw).map(s => ({ tr: String(s?.tr || '').trim(), en: String(s?.en || '').trim() })).filter(s => s.tr)
      if (list.length === 0) { setStatus('error'); setErrorMsg('Could not generate an introduction — try again.'); return }
      setSentences(list); setStatus('ready')
    } catch (e) {
      setStatus('error')
      setErrorMsg((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed.' : (e.message || 'Generation failed.'))
    }
  }

  const toCards = () => sentences.map((s, i) => ({
    sourceId: null, // generated — not a DB node, so it won't try to record to SRS
    source: s.tr, sourceLanguage: code,
    translations: [{ targetId: null, target: s.en, targetLanguage: 'eng' }],
  }))

  const practice = () => { if (onPractice) onPractice(toCards()); close() }

  const saveLesson = async () => {
    setStatus('saving')
    try {
      await addMeetingWithContent({
        variables: {
          input: {
            meeting: {
              title: `${name}'s introduction — ${effLang}`, type: 'Language Class',
              description: `Self-introduction in ${effLang}`, meetingDate: new Date().toISOString().split('T')[0], username,
            },
            sentences: sentences.map(s => ({ sourceText: s.tr, targetText: s.en, sourceLang: code, targetLang: 'eng' })),
            words: [], tasks: [],
          },
        },
      })
      setStatus('saved')
    } catch (e) { setStatus('error'); setErrorMsg(e.message) }
  }

  if (!isOpen) return null
  const hasProfile = !!user?.name

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={close}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()} style={{ maxWidth: '640px' }}>
        <button className={styles.closeBtn} onClick={close}><CloseIcon /></button>

        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Personal</div>
            <h2 className={styles.title}>Introduce yourself</h2>
          </div>
        </div>

        <div className={styles.container}>
          {!hasProfile ? (
            <p style={{ color: '#8a8a95' }}>Fill in your <strong>Profile</strong> first — this drill turns your own details into an introduction to practise.</p>
          ) : status === 'ready' || status === 'saving' || status === 'saved' ? (
            <>
              <p style={{ color: '#8a8a95', margin: '0 0 0.6rem' }}>Your introduction in <strong style={{ color: '#6ee7b7' }}>{effLang}</strong> — tap a line to hear it.</p>
              <div style={{ maxHeight: 300, overflowY: 'auto', background: '#141418', border: '1px solid #2a2a33', borderRadius: 12, padding: '0.8rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {sentences.map((s, i) => (
                  <div key={i} onClick={() => speak(s.tr, voices[code] || voices['eng'])} style={{ cursor: 'pointer', display: 'flex', gap: '0.6rem', alignItems: 'baseline' }}>
                    <VolumeUpIcon style={{ fontSize: '1rem', color: '#6a6a75', flexShrink: 0 }} />
                    <span><span style={{ color: '#e8e8ec', fontWeight: 500 }}>{s.tr}</span> <span style={{ color: '#8a8a95', fontSize: '0.88rem' }}>— {s.en}</span></span>
                  </div>
                ))}
              </div>
              {errorMsg && <div style={{ color: '#f87171', marginTop: '0.5rem' }}>{errorMsg}</div>}
              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                <button className={styles.input} style={{ cursor: 'pointer', width: 'auto', padding: '0.65rem 1.3rem', background: '#34d399', color: '#06281d', fontWeight: 600, border: 'none' }} onClick={practice}>🎤 Practise out loud</button>
                <button className={styles.input} style={{ cursor: 'pointer', width: 'auto', padding: '0.65rem 1.2rem' }} onClick={saveLesson} disabled={status === 'saving' || status === 'saved'}>
                  {status === 'saved' ? 'Saved ✓' : status === 'saving' ? 'Saving…' : 'Save as lesson'}
                </button>
                <button className={styles.input} style={{ cursor: 'pointer', width: 'auto', padding: '0.65rem 1.2rem' }} onClick={generate}>↻ Regenerate</button>
              </div>
            </>
          ) : (
            <>
              <p style={{ color: '#8a8a95', margin: '0 0 0.8rem' }}>Generate a natural self-introduction from your profile — then hear it, practise saying it, or save it as a lesson.</p>
              <div className={styles.field}>
                <div className={styles.fieldLabel}>Language</div>
                <select className={styles.input} value={effLang} onChange={e => setTargetLanguage(e.target.value)}>
                  {(learningNames.length ? learningNames : Object.values(languages)).map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              {status === 'generating' ? (
                <div style={{ textAlign: 'center', padding: '1.5rem 0', color: '#8a8a95' }}>{progress}</div>
              ) : (
                <>
                  {errorMsg && <div style={{ color: '#f87171', marginBottom: '0.5rem' }}>{errorMsg}</div>}
                  <button className={styles.input} style={{ cursor: 'pointer', width: '100%', padding: '0.75rem', background: '#34d399', color: '#06281d', fontWeight: 600, border: 'none' }} onClick={generate}>✦ Write my introduction</button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>, document.body
  )
}
